import {
  createContext, useContext, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';

import TitleModal from '@components/NotifyLoop/TitleModal';
import { logCrmEvent } from '@javascripts/util/eventLogging';

export const logTitleEvent = (event, details = {}) => {
  if (event) logCrmEvent(event.type, { ...event.details, ...details });
};

const logConsentAccepted = (event) => {
  if (event) logCrmEvent('title_consent_accepted', { ...event.details, for_event: event.type });
};

const PASSTHROUGH = {
  go: (href, event = null) => {
    logTitleEvent(event);
    window.location.href = href;
  },
  guard: (_href, event = null) => () => { logTitleEvent(event); },
};

const TitleConsentContext = createContext(PASSTHROUGH);

export const useTitleConsent = () => useContext(TitleConsentContext);

export const TitleConsentProvider = ({ hasConsent = false, phoneNumber = '', children }) => {
  const [consented, setConsented] = useState(Boolean(hasConsent));
  const [pendingHref, setPendingHref] = useState(null);
  const pendingEvent = useRef(null);

  const value = useMemo(() => {
    const hold = (href, event) => {
      pendingEvent.current = event;
      setPendingHref(href);
    };

    return {
      go: (href, event = null) => {
        logTitleEvent(event, { consent_required: !consented });

        if (consented) {
          window.location.href = href;
        } else {
          hold(href, event);
        }
      },
      guard: (href, event = null) => (clickEvent) => {
        logTitleEvent(event, { consent_required: !consented });
        if (consented) return;

        clickEvent.preventDefault();
        hold(href, event);
      },
    };
  }, [consented]);

  return (
    <TitleConsentContext.Provider value={value}>
      {children}
      <TitleModal
        isOpen={Boolean(pendingHref)}
        textConsent={false}
        phoneNumber={phoneNumber}
        close={() => setPendingHref(null)}
        afterSubmit={() => {
          setConsented(true);
          logConsentAccepted(pendingEvent.current);
          window.location.href = pendingHref;
        }}
      />
    </TitleConsentContext.Provider>
  );
};

TitleConsentProvider.propTypes = {
  hasConsent: PropTypes.bool,
  phoneNumber: PropTypes.string,
  children: PropTypes.node,
};

const leaveForm = () => {
  if (document.referrer.startsWith(window.location.origin) && window.history.length > 1) {
    window.history.back();
    return;
  }

  window.location.href = '/title_matches/pledged';
};

export const TitleConsentGate = ({ hasConsent = false, phoneNumber = '' }) => {
  const [open, setOpen] = useState(!hasConsent);
  const accepted = useRef(false);

  return (
    <TitleModal
      isOpen={open}
      textConsent={false}
      phoneNumber={phoneNumber}
      afterSubmit={() => { accepted.current = true; }}
      close={() => {
        setOpen(false);
        if (!accepted.current) leaveForm();
      }}
    />
  );
};

TitleConsentGate.propTypes = {
  hasConsent: PropTypes.bool,
  phoneNumber: PropTypes.string,
};
